import React from 'react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

export interface StatusCard {
    name: string;
    field: string;
    stat: string;
    updated: number;
    total: number;
    filterState: 'updated' | 'not_updated' | 'none';
}

interface StatusCardsProps {
    stats: StatusCard[];
    onCardClick: (field: string) => void;
    desktopColumns?: 2 | 3 | 4 | 5;
    isLoading?: boolean;
    loadingCount?: number;
    loadingLabels?: string[];
}

const getDesktopGridClass = (desktopColumns: number): string => {
    switch (desktopColumns) {
        case 5:
            return 'lg:grid-cols-4 xl:grid-cols-5';
        case 3:
            return 'lg:grid-cols-3';
        case 2:
            return 'lg:grid-cols-2';
        case 4:
        default:
            return 'lg:grid-cols-4';
    }
};

const StatusCards: React.FC<StatusCardsProps> = ({
    stats,
    onCardClick,
    desktopColumns = 4,
    isLoading = false,
    loadingCount,
    loadingLabels = [],
}) => {
    const desktopClass = getDesktopGridClass(desktopColumns);
    const effectiveStats = stats ?? [];

    if (isLoading) {
        const skeletonCount = loadingCount ?? (effectiveStats.length > 0 ? effectiveStats.length : 4);
        const labels = loadingLabels.length > 0 ? loadingLabels : Array.from({ length: skeletonCount }, () => 'Carregando...');

        return (
            <dl className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${desktopClass}`}>
                {Array.from({ length: skeletonCount }).map((_, index) => (
                    <div
                        key={`skeleton-${index}`}
                        className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6"
                    >
                        <div className="animate-pulse space-y-4">
                            <div className="flex justify-between items-start">
                                <p className="truncate text-sm font-medium text-gray-400">{labels[index] ?? 'Carregando...'}</p>
                                <span className="h-5 w-5 rounded-full bg-gray-100" />
                            </div>
                            <div className="h-6 w-24 rounded bg-gray-100" />
                            <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
                                <div className="h-4 w-32 rounded bg-gray-100" />
                            </div>
                        </div>
                    </div>
                ))}
            </dl>
        );
    }

    return (
        <dl className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${desktopClass}`}>
            {effectiveStats.map((item) => {
                const borderClass =
                    item.filterState === 'not_updated' ? 'border-2 border-yellow-500' :
                        item.filterState === 'updated' ? 'border-2 border-green-500' :
                            'border border-transparent';

                return (
                    <div
                        key={item.name}
                        onClick={() => onCardClick(item.field)}
                        className={`group relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow transition hover:shadow-lg sm:px-6 sm:pt-6 cursor-pointer ${borderClass}`}
                    >
                        <dt>
                            <div className="flex justify-between items-start">
                                <p className="truncate text-sm font-medium text-gray-500">{item.name}</p>
                                {item.filterState === 'not_updated' && <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />}
                                {item.filterState === 'updated' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                            </div>
                        </dt>
                        <dd className="flex items-baseline pb-6 sm:pb-7">
                            <p className="text-2xl font-semibold text-gray-900">{item.stat}</p>
                        </dd>
                        <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 transition group-hover:bg-gray-100 sm:px-6">
                            <div className="text-sm">
                                <p className="font-medium text-gray-600">{item.updated} de {item.total} colaboradores</p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </dl>
    );
};

export default StatusCards; 
